// A failed action's toast description (ported from vouchers' friendly-error):
// curated copy per error code, templates reading the problem's params, the
// server's own detail for the long tail. A body without a code (a proxy's,
// or a 405) falls back on its status. Page-load errors stay with describeError.
//
// Adding an entry is a copy commitment — keep the table small.

import { countOf } from "@/lib/plural";
import { describeError } from "./describe-error";
import { ApiError, NO_CODE } from "./error";

export const INTERNAL_ERROR_MESSAGE =
    "Something went wrong on our side. Try again, or contact support with the request ID below.";

export const NOT_FOUND_MESSAGE = "This page or item doesn't exist, or was deleted.";

/** Removing the last key while signed context is required (Precondition.Failed with `keyId`). */
export const LAST_KEY_REFUSAL =
    "Turn off Refuse unsigned context first; with no key, every integration would be refused.";

/** Enabling signed context with no key (Precondition.Failed with `workspaceId`). */
export const NO_KEY_REFUSAL = "Add a public key before refusing unsigned context.";

/** A link-only action on a placement in another mode (Precondition.Failed with `placementId`). */
export const NOT_LINK_REFUSAL = "This placement doesn't use invite links.";

// The summary of a field-errors problem (Kotlin's validation shape, whose
// detail is only "N invalid field(s)"), by code; ewt's strings.
const FIELD_ERRORS_SUMMARY: Partial<Record<string, string>> = {
    "Validation.MissingField": "Some required information is missing.",
    "Validation.InvalidFormat": "Some values aren't in the expected format.",
    "Validation.InvalidValue": "Some values aren't allowed.",
    "Validation.ConstraintViolation": "Some values aren't allowed.",
};

const FIELD_ERRORS_FALLBACK = "Some values aren't valid.";

/** What a problem carrying `errors[]` says above them; undefined without any. */
export function fieldErrorsSummary(error: ApiError): string | undefined {
    if (!error.isValidation) return undefined;
    return FIELD_ERRORS_SUMMARY[error.code] ?? FIELD_ERRORS_FALLBACK;
}

// A template may return undefined to defer to `detail`: curated copy that
// applies only to a recognized params shape.
type Params = Readonly<Record<string, string | null>>;
type Template = string | ((params: Params, error: ApiError) => string | undefined);

const CURATED: Partial<Record<string, Template>> = {
    "Auth.CredentialsExpired": "Your session has expired. Sign in again to continue.",
    "Auth.CredentialsInvalid": "Sign in failed. Check your credentials and try again.",
    "Auth.CredentialsMissing": "You need to sign in to do that.",
    "Access.Forbidden": "You don't have permission to perform this action.",
    "General.NotFound": NOT_FOUND_MESSAGE,
    "System.InternalError": INTERNAL_ERROR_MESSAGE,
    "System.UpstreamUnavailable": "A dependency is currently unavailable. Try again in a moment.",
    "System.DownstreamUnavailable": "A downstream service is currently unavailable. Try again in a moment.",
    "System.Overloaded": "The server is busy. Try again in a moment.",
    "Conflict.AlreadyExists": (p) => `A ${p.entity ?? "record"} with this ${p.field ?? "name"} already exists.`,
    // Deletes refused because of other data, in the params surveys sends.
    "Validation.ConstraintViolation": (p, error) => {
        const surveys = error.countParam("surveyCount");
        if (p.templateId && surveys !== undefined) return `Can't delete — used by ${countOf(surveys, "survey")}.`;
        const responses = error.countParam("responseCount");
        if (p.placementId && responses !== undefined) {
            return `Can't delete — this placement has ${countOf(responses, "response")}.`;
        }
        if (p.surveyId && responses !== undefined) {
            return `Can't delete — this survey has ${countOf(responses, "response")}.`;
        }
        return undefined;
    },
    "Precondition.Failed": (p) => {
        if (p.keyId) return LAST_KEY_REFUSAL;
        if (p.workspaceId) return NO_KEY_REFUSAL;
        if (p.placementId) return NOT_LINK_REFUSAL;
        return undefined;
    },
};

const GATEWAY = new Set([502, 503, 504]);

export function friendlyMessage(error: unknown): string {
    // A failed fetch or an opaque body (a proxy's, while the server is down):
    // describeError already says that one.
    if (!(error instanceof ApiError) || error.opaque) return describeError(error).message;
    // Field errors outrank the code's own copy; without them a validation
    // code's detail is a sentence worth showing.
    const summary = fieldErrorsSummary(error);
    if (summary) return summary;
    if (error.code !== NO_CODE) {
        const template = CURATED[error.code];
        if (typeof template === "string") return template;
        if (typeof template === "function") {
            const curated = template(error.params ?? {}, error);
            if (curated !== undefined) return curated;
        }
        if (error.detail) return error.detail;
    }
    // No code, or a coded problem without detail: the status says it.
    if (error.status >= 500 && !GATEWAY.has(error.status)) return INTERNAL_ERROR_MESSAGE;
    if (error.status < 500 && error.status !== 403 && error.status !== 404 && error.detail) return error.detail;
    return describeError(error).message;
}
